<script lang="ts" setup>
  import { LazyModalSignIn, LazyModalTNC } from '#components';
  import { LS_DISPLAY_NAME_PROMPT } from '#shared/const/cookies';
  import { ESignUpSteps } from '#shared/types/user.types';
  import { MAX_USERNAME_LENGTH } from '#shared/validation';
  import ReCaptcha from '~/components/form/ReCaptcha.vue';
  import { useAppToast } from '~/composables/shared/useAppToast';
  import { useSignUpValidation } from '~/composables/sign-up/useSignUpValidation';
  import { stores } from '~/store';
  import debounce from '~/utils/debounce';

  const { googleRecaptchaEnabled } = useRuntimeConfig().public;
  const recaptchaEnabled = googleRecaptchaEnabled;
  const recaptcha = useTemplateRef('recaptcha');

  const { open: openModal, close: closeModal } = stores.modals;

  const bidr = useBidrPixel();
  const { showErrorToast, showSuccessToast } = useAppToast();

  const isSubmitLoading = ref(false);

  const { credentials, activeStep } = storeToRefs(stores.signUp);
  const { validate, validateField, checkEmailAvailable, validationErrorsMap } = useSignUpValidation(credentials);

  const showPassword = ref(false);
  const showAdultTooltip = ref(false);
  const showTncTooltip = ref(false);

  const tooltipUiSettings = {
    content: 'text-white font-barlow text-base',
  };

  watchEffect(() => {
    credentials.value.affiliateCode = credentials.value.affiliateCode?.toUpperCase() || '';
    credentials.value.email = credentials.value.email?.toLowerCase() || '';
  });

  async function submitForm() {
    try {
      isSubmitLoading.value = true;
      const isSuccess = await stores.signUp.createUser();

      if (isSuccess) {
        bidr.fetchRegistrationPixel();

        if (credentials.value.viaProvider) {
          stores.user.setSocialSignUpIsPending(false);
          await stores.user.continueLoggingInUser();

          localStorage.setItem(`${LS_DISPLAY_NAME_PROMPT}${stores.user.getUserId()}`, 'true');
          activeStep.value = ESignUpSteps.DisplayNamePrompt;
        } else {
          activeStep.value = ESignUpSteps.EmailConfirmation;
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      isSubmitLoading.value = false;
    }
  }

  async function signUpWithGoogle() {
    try {
      stores.user.setSocialSignUpIsPending(true);

      const { $firebase } = useTypedNuxtApp();

      const provider = new $firebase.instance.auth.GoogleAuthProvider();
      const { user } = await $firebase.auth?.signInWithPopup(provider);

      if (user?.email) {
        const emailIsAvailable = await checkEmailAvailable(user.email);

        if (emailIsAvailable) {
          credentials.value.email = user.email;
          credentials.value.isAdult = true;
          credentials.value.isAgreeWithTnc = true;
          credentials.value.viaProvider = true;

          await submitForm();
        } else {
          showSuccessToast('Email is already registered', 'Logging you in');
          stores.user.setSocialSignUpIsPending(false);
          stores.modals.close('sign-up');
          await stores.user.continueLoggingInUser();
        }
      } else {
        showErrorToast('Sign up with Google failed');
        await stores.signUp.handleSignOut();
      }
    } catch (error: any) {
      showErrorToast(error.message);
      await stores.signUp.handleSignOut();
    } finally {
      stores.user.setSocialSignUpIsPending(false);
    }
  }

  async function verifyRecaptcha(token: string, isGoogleSignUp: boolean = false) {
    credentials.value.googleToken = token;

    if (isGoogleSignUp) {
      await signUpWithGoogle();
    } else {
      await submitForm();
    }
  }

  const debounceValidateField = debounce(validateField, 250);

  async function handleSubmit(event: Event, isGoogleSignUp: boolean = false) {
    if (isGoogleSignUp) {
      await validate(credentials.value);

      if (validationErrorsMap.value.has('isAdult') || validationErrorsMap.value.has('isAgreeWithTnc')) {
        showAdultTooltip.value = validationErrorsMap.value.has('isAdult');
        showTncTooltip.value = validationErrorsMap.value.has('isAgreeWithTnc');
        return;
      }
    }

    if (recaptchaEnabled) {
      recaptcha.value?.execute(isGoogleSignUp);
    } else if (isGoogleSignUp) {
      await signUpWithGoogle();
    } else {
      await submitForm();
    }
  }

  async function openSignIn() {
    if (import.meta.server) return;

    closeModal('sign-up');
    await openModal('sign-in', LazyModalSignIn);
  }

  async function openTNCModal() {
    await openModal('tnc-modal', LazyModalTNC);
  }
</script>

<template>
  <UForm :state="credentials" :validate="validate" class="w-full flex flex-col gap-4" @submit="handleSubmit">
    <button class="opacity-0 w-0 h-0 absolute" autofocus aria-hidden="true" />

    <UFormField
      :ui="{
        label: 'text-white font-barlow text-sm font-medium leading-snug pb-1',
        error: 'hidden',
      }"
      label="Username"
      name="username"
      required
      eager-validation
    >
      <CoreInput
        v-model="credentials.username"
        :maxlength="MAX_USERNAME_LENGTH"
        variant="subtle"
        :is-invalid="validationErrorsMap.get('username')"
        @input="debounceValidateField('username')"
      />
    </UFormField>

    <UFormField
      :ui="{
        label: 'text-white font-barlow text-sm font-medium leading-snug pb-1',
        error: 'hidden',
      }"
      label="Email"
      name="email"
      required
      eager-validation
    >
      <CoreInput
        v-model="credentials.email"
        variant="subtle"
        :is-invalid="validationErrorsMap.get('email')"
        @input="debounceValidateField('email')"
      />
    </UFormField>
    <UFormField
      :ui="{
        label: 'text-white font-barlow text-sm font-medium leading-snug pb-1',
        error: 'hidden',
      }"
      label="Password"
      name="password"
      required
      eager-validation
    >
      <CoreInput
        v-model="credentials.password"
        :type="showPassword ? 'text' : 'password'"
        variant="subtle"
        @input="debounceValidateField('email')"
      >
        <template #trailing>
          <UButton
            :icon="showPassword ? 'i-hugeicons:view-off-slash' : 'i-hugeicons:view'"
            aria-label="Toggle password visibility"
            class="text-[#101317] cursor-pointer"
            color="neutral"
            size="xl"
            variant="link"
            @click="showPassword = !showPassword"
          />
        </template>
      </CoreInput>

      <div class="w-full flex justify-between">
        <span class="text-white font-barlow text-sm font-normal leading-[150%] mt-1">
          At least 8 characters, 1 upper case, 1 special character
        </span>
      </div>
    </UFormField>

    <UFormField
      :ui="{
        label: 'text-white font-barlow text-sm font-medium leading-snug pb-1',
        error: 'hidden',
      }"
      name="affiliateCode"
      eager-validation
    >
      <CoreInput
        v-model="credentials.affiliateCode"
        variant="subtle"
        placeholder="Affiliate Code (Optional)"
        base-styles="placeholder:text-[#101317]"
        :is-invalid="validationErrorsMap.get('affiliateCode')"
        @input="debounceValidateField('affiliateCode')"
      />
    </UFormField>

    <UFormField :ui="{ error: 'hidden' }" name="isAdult">
      <UTooltip
        v-model:open="showAdultTooltip"
        text="You have to confirm your age"
        arrow
        :ui="tooltipUiSettings"
        :content="{
          align: 'center',
          sideOffset: 0,
        }"
      >
        <CoreCheckbox
          v-model="credentials.isAdult"
          required
          :base-class="validationErrorsMap.get('isAdult') ? 'red-shadow' : ''"
          @input="debounceValidateField('isAdult')"
        >
          <template #label>
            <span class="text-white text-md font-barlow text-base font-normal leading-[150%]">
              I confirm that I am 18 years or older
            </span>
          </template>
        </CoreCheckbox>
      </UTooltip>
    </UFormField>

    <UFormField :ui="{ error: 'hidden' }" name="isAgreeWithTnc">
      <UTooltip
        v-model:open="showTncTooltip"
        text="You have to agree to our Terms and Conditions"
        arrow
        :ui="tooltipUiSettings"
        :content="{
          align: 'center',
          sideOffset: 0,
        }"
      >
        <CoreCheckbox
          v-model="credentials.isAgreeWithTnc"
          required
          :base-class="validationErrorsMap.get('isAgreeWithTnc') ? 'red-shadow' : ''"
          @input="debounceValidateField('isAgreeWithTnc')"
        >
          <template #label>
            <span class="text-white text-md font-barlow text-base font-normal leading-[150%]">
              I agree to the&nbsp;
            </span>
            <NuxtLink
              class="text-primary font-medium cursor-pointer underline inline-block"
              data-test-id="terms-condition-link"
              @click.prevent="openTNCModal"
            >
              Terms & Conditions
            </NuxtLink>
          </template>
        </CoreCheckbox>
      </UTooltip>
    </UFormField>

    <CoreButton class="mb-[-25px]" type="submit" :loading="isSubmitLoading">Sign Up</CoreButton>
  </UForm>

  <ReCaptcha v-if="recaptchaEnabled" ref="recaptcha" @verify="verifyRecaptcha" />

  <div class="w-full flex items-center justify-center gap-2.5 mt-6">
    <div class="basis-full h-[1px] w-full bg-white/30" />
    <span
      class="basis-full text-white/70 text-center font-barlow text-sm font-normal leading-[12px] whitespace-nowrap translate-y-[-1px]"
    >
      or sign up with Google
    </span>
    <div class="basis-full h-[1px] w-full bg-white/30" />
  </div>

  <CoreButton
    :loading="stores.user.socialSignUpIsPending"
    class="w-full bg-white hover:bg-white/90 active:bg-white/80 disabled:bg-white/80 flex items-center justify-center gap-2.5 h-11"
    @click="handleSubmit($event, true)"
  >
    <template #default>
      <SvgoGoogle class="m-0 mb-[-1px] size-6" />
      <span class="text-[#101317] font-barlow text-lg font-semibold leading-[16px]">Sign Up with Google</span>
    </template>
  </CoreButton>

  <p class="m-0 p-0 text-white font-barlow text-base font-normal leading-[1.5]">
    Already have an account?
    <span class="text-[#0080FF] font-medium underline cursor-pointer" @click="openSignIn">Login here</span>
  </p>
</template>
